import { useState, useEffect, useRef, useCallback, useMemo } from "react";

import { PortalModal } from "../PortalModal/PortalModal";
import PortalModalHeader from "../PortalModal/PortalModalHeader";
import PortalModalContent from "../PortalModal/PortalModalContent";
import PortalModalFooter from "../PortalModal/PortalModalFooter";
import { ModalButton } from "../PortalModal/ModalButton";
import { CloseButton } from "../PortalModal/CloseButton";

import { TabButton } from "../Misc/TabsNavigation/TabButton";
import { TabsContainer } from "../Misc/TabsNavigation/TabsContainer";
import ChartTabContent from "./ChartTabContent";
import moment from "moment";
import toast from "react-hot-toast";

import {
  DeviceNode,
  DynamicTab,
  ReportConfigParams,
  ReportConfig,
  DateRange,
} from "../../types/types";
import { useTranslation } from "react-i18next";
import { TextInput } from "../Misc/TextInput";
import { Requester } from "../../utils/Requester";

interface TabState {
  [key: number]: ReportConfigParams;
}

interface Props {
  reportConfig?: ReportConfig;
  nodes: DeviceNode[];
  dateRange: DateRange;
  installationId: number;
  refreshConfigs: () => void;
  configNames: Array<string>;
}

export function ReportConfigModal({
  reportConfig,
  nodes,
  dateRange,
  installationId,
  refreshConfigs,
  configNames,
}: Props) {
  const [showModal, setShowModal] = useState(true);
  const [configName, setConfigName] = useState(reportConfig?.name);

  const { t } = useTranslation();

  const [tabs, setTabs] = useState<DynamicTab[]>([{ id: 1, title: t("Chart 1") }]);
  const [selectedTab, setSelectedTab] = useState<number>(1);
  // Have the next tab id as a separate counter to ensure that new tabs always
  // get a unique ID
  const nextTabIdRef = useRef<number>(2);

  // Tab and the state of the tab are stored separately
  const [tabStates, setTabStates] = useState<TabState>({});

  const decodeReportConfig = useCallback((
    deviceNodes: DeviceNode[],
    chartParam?: string,  // If chart param is not passed it means a new config is being created
  ): ReportConfigParams => {
    const params = new URLSearchParams(chartParam);
    const decodedNodes = JSON.parse(params.get("nodes") || "[]")
    return {
      timezone: params.get("timezone") || moment.tz.guess(),
      x1: dateRange.start,
      x2: dateRange.end,
      nodes: deviceNodes.map(node => {
        const nodeId = node.id;
        const isSelected = decodedNodes ? decodedNodes[nodeId]?.length > 0 : false;
        return {
          selected: isSelected,
          id: node.id,
          local_id: node.local_id,
          sensor_name: node.sensor_name,
          node_type: node.node_type,
          probe_type: "probe_type" in node ? node.probe_type : undefined,
          removed_time: node.removed_time,
          fields: node.fields.map(field => ({
            front_end_name: field.front_end_name,
            sensor_color: field.sensor_color,
            sens_type: field.sens_type,
            enabled: decodedNodes[nodeId]?.includes(field.sens_type) || false
          }))
        };
      })
    };
  },[dateRange.start, dateRange.end]);

  // On open decode chart params
  useEffect(() => {
    if (showModal) {
      if (reportConfig) {
        // Initialize tabs and tab states
        const newTabs = reportConfig.chart_params.map((_, index) => ({
          id: index + 1,
          title: t("Chart {{n}}", { n: index + 1 })
        }));

        const newTabStates: TabState = {};
        reportConfig.chart_params.forEach((chartParam, index) => {
          newTabStates[index + 1] = decodeReportConfig(nodes, chartParam);
        });
        setTabs(newTabs);
        setTabStates(newTabStates);
        nextTabIdRef.current = reportConfig.chart_params.length + 1;
      } else {
        // New config case
        setTabs([{ id: 1, title: t("Chart {{n}}", { n: 1 }) }]);
        setTabStates({
          1: decodeReportConfig(nodes)
        });
        nextTabIdRef.current = 2;
      }
      setSelectedTab(1);
      setConfigName(reportConfig?.name)
    }
  }, [showModal, nodes, reportConfig, t, decodeReportConfig]);

  const onNodesChange = useCallback(
    (updatedNodes: ReportConfigParams) => {
      setTabStates((prevStates) => ({
        ...prevStates,
        [selectedTab]: updatedNodes,
      }));
    },
    [selectedTab]
  );

  function handleAddTab() {
    const newTabId = nextTabIdRef.current;
    const newTab = { id: newTabId, title: t("Chart {{n}}", { n: tabs.length + 1 }) };
    setTabs([...tabs, newTab]);
    setTabStates((prevStates) => ({
      ...prevStates,
      [newTabId]: decodeReportConfig(nodes)
    }));
    setSelectedTab(newTabId);
    nextTabIdRef.current += 1;
  };

  const handleCloseTab = (tabId: number) => {
    const updatedTabs = tabs
      .filter((tab) => tab.id !== tabId)
      .map((tab, index) => ({
        ...tab,
        title: index === 0 ? tab.title : t("Chart {{n}}", { n: index + 1 }),
      }));

    setTabs(updatedTabs);

    setTabStates((prevStates) => {
      const newStates = { ...prevStates };
      delete newStates[tabId];
      return newStates;
    });

    if (selectedTab === tabId) {
      setSelectedTab(1);
    }
  };

  const isSensorSelected = useMemo(() => {
    return Object.values(tabStates).every((tabState: ReportConfigParams) =>
      tabState.nodes.some((node) =>
        node.fields.some((field) => field.enabled)
      )
    );
  }, [tabStates])

  function getParamStrings() {
    const chartParamsArray: string[] = [];

    Object.keys(tabStates).forEach((tabId) => {
      const tabState = tabStates[Number(tabId)];
      const tabNodes = tabState.nodes;

      // Transform nodes to SelectedNodeSensors type
      const selectedNodes: Record<number, string[]> = tabNodes.reduce((acc, node) => {
        if (node.selected) {
          acc[node.id] = node.fields
            .filter(field => field.enabled)
            .map(field => field.sens_type);
        }
        return acc;
      }, {} as Record<number, string[]>);

      // Create URLSearchParams with all the necessary parameters
      const params = new URLSearchParams();
      params.set('nodes', JSON.stringify(selectedNodes));

      chartParamsArray.push(params.toString());
    });

    return chartParamsArray;
  }

  const handleEdit = (name: string, params: Array<string>) => {
    if (reportConfig) {
      Requester.updateReportConfig(reportConfig.id, {
        name: name,
        chart_params: params,
      }).then(() => {
        toast.success(t("Success!"));
        refreshConfigs();
        setShowModal(false);
      })
    }
  };

  const handleCreate = (name: string, params: Array<string>) => {
    Requester.createReportConfig(installationId, {
      name: name,
      chart_params: params,
    }).then(() => {
      toast.success(t("Success!"));
      refreshConfigs();
      setShowModal(false);
    })
  };

  const isNameDuplciate = useMemo(() => {
    if (!configName) return false;
    if (reportConfig && configName === reportConfig.name) return false;
    return configNames.includes(configName.trim());
  }, [configName, configNames, reportConfig]);

  return <>
    <PortalModal isOpen={showModal} close={() => setShowModal(false)}>
      <PortalModalHeader
        title={
          reportConfig
            ? t("Edit Report Configuration")
            : t("Create a new Report Configuration")
        }
        close={() => setShowModal(false)}
      />
      <div style={{
        padding: "1rem 1rem 0 1rem",
        borderBottom: "1px solid #e3e3e3",
        width: "56rem",
      }}>
        <TabsContainer>
          {tabs.map((tab) => (
            <TabButton
              key={tab.id}
              title={tab.title}
              selected={selectedTab === tab.id}
              onClick={() => setSelectedTab(tab.id)}
              onClose={tab.id !== 1
                ? () => handleCloseTab(tab.id)
                : undefined
              }
            />
          ))}
          <TabButton
            title={t("+ Add")}
            onClick={handleAddTab}
            selected={false}
          />
        </TabsContainer>
      </div>
      <PortalModalContent>
        {tabStates[selectedTab] && (
          <ChartTabContent
            reportConfig={tabStates[selectedTab]}
            onNodesChange={onNodesChange}
          />
        )}
        <br />
        <div>
          <p>{t("Configuration name (will not appear in the report)")}</p>
          <TextInput
            value={configName || ""}
            onChange={setConfigName}
            placeholder={t("Enter configuration name")}
            maxLength={40}
          />
          {isNameDuplciate && (
            <p style={{ color: "red", marginTop: "1rem" }}>
              {t("Configuration with this name already exists")}
            </p>
          )}
        </div>
      </PortalModalContent>
      <PortalModalFooter>
        <ModalButton
          disabled={!isSensorSelected || !configName || isNameDuplciate}
          title={
            [
              !isSensorSelected && t("No data selected"),
              !configName && t("Configuration name is required"),
              isNameDuplciate && t("Configuration with this name already exists"),
            ].filter(Boolean).join("\n")
          }
          onClick={() =>
            reportConfig
              ? handleEdit(configName || "", getParamStrings())
              : handleCreate(configName || "", getParamStrings())
          }
        >
          {t("Save")}
        </ModalButton>
        <CloseButton close={() => setShowModal(false)} />
      </PortalModalFooter>
    </PortalModal>
  </>
}